import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface TOTPEnrollData {
  id: string;
  type: 'totp';
  totp: {
    qr_code: string;   // SVG QR code
    secret: string;    // base32 secret for manual entry
    uri: string;       // otpauth:// URI
  };
}

export const useMFA = () => {
  const [loading, setLoading] = useState(false);

  /** Démarre l'enrollment TOTP — retourne QR code SVG + secret + factorId */
  const enrollTOTP = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        issuer: 'Nazandoo',
      });
      return { data: data as TOTPEnrollData | null, error };
    } finally {
      setLoading(false);
    }
  }, []);

  /** Crée un challenge puis vérifie le code TOTP (enrollment ou login) */
  const challengeAndVerify = useCallback(async (factorId: string, code: string) => {
    setLoading(true);
    try {
      const { data: challengeData, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) return { data: null, error: challengeError };

      const { data, error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });
      return { data, error };
    } finally {
      setLoading(false);
    }
  }, []);

  /** Supprime un facteur TOTP (désactivation MFA par l'utilisateur) */
  const unenroll = useCallback(async (factorId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.unenroll({ factorId });
      return { data, error };
    } finally {
      setLoading(false);
    }
  }, []);

  /** Liste tous les facteurs TOTP de l'utilisateur connecté */
  const listFactors = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    return { data, error };
  }, []);

  /** Retourne le niveau d'assurance actuel (aal1 | aal2) */
  const getAAL = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    return { data, error };
  }, []);

  return { enrollTOTP, challengeAndVerify, unenroll, listFactors, getAAL, loading };
};
